import React, { useState, useEffect, useMemo } from "react";
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, TextField, Typography } from "@mui/material";

// Icons
import { useMessage } from "../../context/message-context";
import apiRequest from "../../functions/api";
import { useTranslation } from "react-i18next";
import { FormAutoComplete, FormNumField, FormTextField } from "./FormKomponents";
import { useSessionStorage } from "../../utils/hooks";

const program = "KMPAPI";

// ----------------------------------------------------
// ------------------Haupt Komponente-----------------
// ----------------------------------------------------
function KabelEditDialog({ kabelId, open = false, optionen, handleClose, accessToken, disabled = false, dialogVersion, client }) {
  // dialogVersion States: 0=Bearbeiten, 1=Informationen, 2=Erstellen

  const { t } = useTranslation();
  const [formValues, setFormValues] = useState(() => {
    try {
      const stored = sessionStorage.getItem("KabelFastForm");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccessMessage, showErrorMessage } = useMessage();

  // Texte je nach Version
  const Titel = dialogVersion === 0 ? "Kabel bearbeiten" : dialogVersion === 1 ? "Informationen" : dialogVersion === 2 ? "Kabel erstellen" : "";
  const editId = dialogVersion === 0 ? true : dialogVersion === 1 ? true : dialogVersion === 2 ? false : true;
  const endpunkt = dialogVersion === 0 ? "KABEL_EDIT" : dialogVersion === 1 ? "KABEL_EDIT" : dialogVersion === 2 ? "KABEL_ERSTELLEN" : true;

  // Optionen für Select Felder
  const einheitenOptions = useMemo(() => {
    return optionen?.einheiten;
  });

  const artenOptions = useMemo(() => {
    return optionen?.art;
  });

  const kategorieOptions = useMemo(() => {
    return optionen?.kategorie;
  });

  const spezOptions = useMemo(() => {
    return optionen?.spez;
  });

  const standortOptions = useMemo(() => {
    return optionen?.standort;
  });

  const steckerOptions = useMemo(() => {
    return optionen?.stecker;
  });

  // Filtern der Option passend zu Auswahl der Kategorie
  const deviceOptionsStart = useMemo(() => {
    if (!optionen || !formValues.startKategorie) return [];
    return optionen?.device?.filter((device) => device.kat === formValues.startKategorie);
  }, [optionen, formValues.startKategorie, kategorieOptions]);

  const deviceOptionsZiel = useMemo(() => {
    if (!optionen || !formValues.zielKategorie) return [];
    return optionen?.device?.filter((device) => device.kat === formValues.zielKategorie);
  }, [optionen, formValues.zielKategorie, kategorieOptions]);

  const virtuellOptionen = useMemo(() => {
    return [
      { value: "0", label: "Nicht Virtuell" },
      { value: "1", label: "Virtuell" },
    ];
  });

  const nextKey = useMemo(() => {
    return optionen?.nextKey ? optionen?.nextKey : "001";
  }, [optionen]);

  // ----------------------------------------------------
  // -----------Allgemeine funktionen--------------------
  // ----------------------------------------------------
  useEffect(() => {
    if (kabelId != undefined && kabelId != "" && kabelId != null && open == true) {
      setIsLoading(true);
      const headers = {
        Authorization: "Bearer " + accessToken,
      };

      const payload = {
        mandant: client,
        program: program,
        id: kabelId,
        statement: "KABEL_DETAILS",
      };

      apiRequest("POST", "daten", payload, headers, datenCb, errorCb);
    } 
  }, [kabelId, open]);

  useEffect(() => {
    setFormValues((prev) => ({
      ...prev,
      ID: nextKey,
    }));
  }, [nextKey]);

  useEffect(() => {
    useSessionStorage;
    sessionStorage.setItem("KabelForm", JSON.stringify(formValues));
  }, [formValues]);

  const datenCb = (response) => {
    setIsLoading(false);
    if (response.status === 200) {
      setFormValues(response.data.kabel);
    } else {
      showErrorMessage(response.data.message);
    }
  };

  const errorCb = () => {
    showErrorMessage(t("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    const payload = {
      mandant: client,
      program: program,
      statement: endpunkt,
      formValues: formValues,
    };

    apiRequest("POST", "daten", payload, headers, submitCb, errorCb);
  };

  const submitCb = (response) => {
    if (response.status === 200) {
      showSuccessMessage("Änderungen erfolgreich gespeichert");
      sessionStorage.setItem("KabelForm", "");
      clearForm();
      handleClose();
    } else {
      showErrorMessage(response.data.message);
    }
  };

  const handleChange = (event, newValue, newName) => {
    const { name, value } = event.target;
    if (name) {
      setFormValues((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (newValue?.key) {
      setFormValues({ ...formValues, [newName]: newValue?.key });
    }
  };

    const clearForm = () => {
    setFormValues({
      ID: nextKey,
      startStandort: null,
      startRaum: null,
      startEinheit: null,
      startKategorie: null,
      startFhw: null,
      startPort: null,
      zielStandort: null,
      zielRaum: null,
      zielEinheit: null,
      zielKategorie: null,
      zielFhw: null,
      zielPort: null,
      art: null,
      laenge: "00.0",
      startStecker: null,
      zielStecker: null,
      spez: null,
      virtuell: "0",
    });
  };

  return (
    <>
      {!isLoading && formValues && (
        <Dialog fullWidth={true} maxWidth="lg" open={open} onClose={handleClose}>
          <DialogTitle>{t(Titel)}</DialogTitle>

          <DialogContent>
            <form onSubmit={handleSubmit} id="edit-form">
              <Typography>{t("Nächste freie ID: ") + nextKey}</Typography>
              <FormTextField label="Kabel ID" indexKey={"ID"} required disabled={editId} formValues={formValues} handleChange={handleChange} />

              <Typography sx={{ paddingTop: 2, fontWeight: "bold" }}>{t("Kabel Start")}</Typography>
              <Divider sx={{ borderBottomWidth: 5 }} component="div" />

              <FormAutoComplete
                label="Start Standort"
                indexKey={"startStandort"}
                options={standortOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />
              <FormAutoComplete
                label="Start Raum"
                indexKey={"startRaum"}
                options={standortOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />
              <FormAutoComplete
                label="Start Einheit"
                indexKey={"startEinheit"}
                options={einheitenOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />
              <FormAutoComplete
                label="Start Kategorie"
                indexKey={"startKategorie"}
                options={kategorieOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />
              <FormAutoComplete
                label="Start Gerät"
                indexKey={"startFhw"}
                options={deviceOptionsStart}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />
              <FormTextField label="Start Port" indexKey={"startPort"} required disabled={disabled} formValues={formValues} handleChange={handleChange} />
              <FormAutoComplete
                label="Start Stecker"
                indexKey={"startStecker"}
                options={steckerOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />

              <Typography sx={{ paddingTop: 2, fontWeight: "bold" }}>{t("Kabel Ziel")}</Typography>
              <Divider sx={{ borderBottomWidth: 5 }} component="div" />

              <FormAutoComplete
                label="Ziel Stanrdort"
                indexKey={"zielStandort"}
                options={standortOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />
              <FormAutoComplete
                label="Ziel Raum"
                indexKey={"zielRaum"}
                options={standortOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />
              <FormAutoComplete
                label="Ziel Einheit"
                indexKey={"zielEinheit"}
                options={einheitenOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />
              <FormAutoComplete
                label="Ziel Kategorie"
                indexKey={"zielKategorie"}
                options={kategorieOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />
              <FormAutoComplete
                label="Ziel Gerät"
                indexKey={"zielFhw"}
                options={deviceOptionsZiel}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />
              <FormTextField label="Ziel Port" indexKey={"zielPort"} required disabled={disabled} formValues={formValues} handleChange={handleChange} />
              <FormAutoComplete
                label="Ziel Stecker"
                indexKey={"zielStecker"}
                options={steckerOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />

              <Typography sx={{ paddingTop: 2, fontWeight: "bold" }}>{t("Kabel Infos")}</Typography>
              <Divider sx={{ borderBottomWidth: 5 }} component="div" />

              <FormAutoComplete
                label="Kabel Art"
                indexKey={"art"}
                options={artenOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />

              <FormNumField label="Kabel Länge (in Metern)" indexKey={"laenge"} formValues={formValues} disabled={disabled} setFormValues={setFormValues} />

              <FormAutoComplete
                label="Spezifikation"
                indexKey={"spez"}
                options={spezOptions}
                required
                disabled={disabled}
                formValues={formValues}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />

              <FormAutoComplete
                label="Virtuelles Kabel"
                indexKey={"virtuell"}
                options={virtuellOptionen}
                formValues={formValues}
                disabled={disabled}
                setFormValues={setFormValues}
                dialogVersion={dialogVersion}
              />
            </form>
          </DialogContent>

          <DialogActions>
            {!disabled && (
              <>
                <Button variant="outlined" color="secondary" onClick={handleClose}>
                  Abbrechen
                </Button>
                <Button variant="contained" color="primary" type="submit" form="edit-form">
                  Speichern
                </Button>
              </>
            )}
            {disabled && (
              <Button variant="contained" color="primary" onClick={handleClose}>
                Schließen
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

export default KabelEditDialog;
